import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, CurrencyCircleDollar } from "@phosphor-icons/react";

const COMPANY_ROLES = [
  { key: "ana_yuklenici", label: "Ana Yüklenici" },
  { key: "alt_yuklenici", label: "Alt Yüklenici" },
  { key: "danisman", label: "Danışman" },
  { key: "musavir", label: "Müşavir" },
];

const CURRENCIES = [
  { key: "TRY", label: "₺ TRY" },
  { key: "USD", label: "$ USD" },
  { key: "EUR", label: "€ EUR" },
];

const empty = {
  employer_name: "",
  company_role: "ana_yuklenici",
  main_contractor_name: "",
  start_date: "",
  end_date: "",
  contract_amount: "",
  contract_currency: "TRY",
  project_type_key: "",
  project_subtype_key: "",
  notes: "",
};

/**
 * Proje bilgileri dialog'u. `site` prop'u aktifse açılır; kapanınca `onClose()`.
 * Kaydettikten sonra `onSaved(updatedSite)` çağrılır.
 */
export default function ProjectInfoDialog({ site, onClose, onSaved }) {
  const [projectTypes, setProjectTypes] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!site) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [pt, sp] = await Promise.all([
          axios.get(`${API}/project-types`),
          axios.get(`${API}/sites/${site.id}/project`),
        ]);
        if (!mounted) return;
        setProjectTypes(pt.data);
        const p = sp.data?.project;
        if (p) {
          setForm({
            employer_name: p.employer_name || "",
            company_role: p.company_role || "ana_yuklenici",
            main_contractor_name: p.main_contractor_name || "",
            start_date: p.start_date || "",
            end_date: p.end_date || "",
            contract_amount: p.contract_amount || "",
            contract_currency: p.contract_currency || "TRY",
            project_type_key: p.project_type_key || "",
            project_subtype_key: p.project_subtype_key || "",
            notes: p.notes || "",
          });
        } else {
          setForm(empty);
        }
      } catch (e) {
        toast.error(formatErr(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [site]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    // Ana tip değişirse alt tipi sıfırla
    if (k === "project_type_key") next.project_subtype_key = "";
    // Ana yüklenici ise main_contractor_name'i temizle (proje bizim, ana yüklenici de biziz)
    if (k === "company_role" && v === "ana_yuklenici") next.main_contractor_name = "";
    return next;
  });

  const selectedType = projectTypes.find(t => t.key === form.project_type_key);
  const subtypes = selectedType?.subtypes || [];
  const requiresMainContractor = form.company_role !== "ana_yuklenici";

  const save = async () => {
    if (!form.employer_name.trim()) { toast.error("İşveren adı zorunlu"); return; }
    if (!form.project_type_key) { toast.error("Proje tipi seçilmeli"); return; }
    if (requiresMainContractor && !form.main_contractor_name.trim()) {
      toast.error("Ana yüklenici adı zorunlu (bu firma ana yüklenici değil)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        contract_amount: parseFloat(form.contract_amount) || 0,
      };
      const { data } = await axios.patch(`${API}/sites/${site.id}/project`, payload);
      toast.success("Proje bilgileri kaydedildi");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!site) return null;

  return (
    <Dialog open={!!site} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="rounded-none sm:rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="project-info-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-bold tracking-tight font-heading flex items-center gap-2">
            <Briefcase size={20} weight="duotone" className="text-[#95C11F]" />
            Proje Bilgileri — {site.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600">
            Bu şantiyeye özel proje tanımı. Etap/blok yapısı ve iş kırınımı ilerleyen fazlarda üstüne kurulacak.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-sm text-slate-500">Yükleniyor…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İşveren</Label>
              <Input
                value={form.employer_name}
                data-testid="proj-employer"
                onChange={e => set("employer_name", e.target.value)}
                placeholder="İş sahibinin ad/ünvanı"
                className="mt-2 rounded-none sm:rounded-sm"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma Rolü</Label>
              <Select value={form.company_role} onValueChange={v => set("company_role", v)}>
                <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="proj-company-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_ROLES.map(r => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">
                Ana Yüklenici {requiresMainContractor && <span className="text-red-600">*</span>}
              </Label>
              <Input
                value={form.main_contractor_name}
                data-testid="proj-main-contractor"
                onChange={e => set("main_contractor_name", e.target.value)}
                disabled={!requiresMainContractor}
                placeholder={requiresMainContractor ? "Ana yüklenici firma adı" : "(Bu firma ana yüklenici)"}
                className="mt-2 rounded-none sm:rounded-sm disabled:bg-slate-50"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={form.start_date}
                data-testid="proj-start"
                onChange={e => set("start_date", e.target.value)}
                className="mt-2 rounded-none sm:rounded-sm"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Bitiş Tarihi</Label>
              <Input
                type="date"
                value={form.end_date}
                data-testid="proj-end"
                onChange={e => set("end_date", e.target.value)}
                className="mt-2 rounded-none sm:rounded-sm"
              />
            </div>

            <div className="md:col-span-2 grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700 flex items-center gap-1.5">
                  <CurrencyCircleDollar size={14} weight="duotone" className="text-[#95C11F]" />
                  Toplam Sözleşme Bedeli
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.contract_amount}
                  data-testid="proj-amount"
                  onChange={e => set("contract_amount", e.target.value)}
                  className="mt-2 rounded-none sm:rounded-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Para Birimi</Label>
                <Select value={form.contract_currency} onValueChange={v => set("contract_currency", v)}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="proj-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Proje Tipi</Label>
              <Select value={form.project_type_key} onValueChange={v => set("project_type_key", v)}>
                <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="proj-type">
                  <SelectValue placeholder="Tip seçin" />
                </SelectTrigger>
                <SelectContent>
                  {projectTypes.map(t => (
                    <SelectItem key={t.key} value={t.key}>
                      <div className="flex items-center gap-2">
                        <span>{t.label}</span>
                        {t.is_system && <span className="text-[9px] uppercase tracking-widest bg-slate-200 text-slate-600 px-1 py-0.5 font-bold">Sistem</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">
                Alt Tip {subtypes.length > 0 && <span className="text-slate-400">({subtypes.length})</span>}
              </Label>
              <Select
                value={form.project_subtype_key}
                onValueChange={v => set("project_subtype_key", v)}
                disabled={!form.project_type_key || subtypes.length === 0}
              >
                <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="proj-subtype">
                  <SelectValue placeholder={
                    !form.project_type_key ? "Önce tip seçin"
                      : subtypes.length === 0 ? "Alt tip tanımlanmamış"
                      : "Alt tip seçin"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {subtypes.map(st => (
                    <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
              <Textarea
                value={form.notes}
                data-testid="proj-notes"
                onChange={e => set("notes", e.target.value)}
                rows={2}
                className="mt-2 rounded-none sm:rounded-sm"
              />
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onClose?.()}
                  className="rounded-none sm:rounded-sm">
            İptal
          </Button>
          <Button onClick={save} disabled={loading || saving}
                  data-testid="proj-save"
                  className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
